import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Empty, Popconfirm, Space, Spin, Tag, Tooltip, Typography, message } from 'antd'
import { BorderOuterOutlined, CloseOutlined, ExpandOutlined, UndoOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { reviewMapApi } from '@/api/reviewMap'
import {
  SPACE_TYPE_COLORS,
  SPACE_TYPE_LABELS,
  type ReviewMapBBox,
  type ReviewMapSpace,
  type SpaceType,
  type UUID,
} from '@/types/api'

const { Text } = Typography

interface Props {
  jobId: UUID
  selectedKey: string | null
  focusSeq: number
  onSelect: (spaceKey: string) => void
  onShopfrontApplied?: () => void
}

interface ViewBox {
  minX: number
  minY: number
  width: number
  height: number
}

const ZOOM_PAD = 0.45
const MIN_POLYGON_POINTS = 3

function bboxToViewBox(b: ReviewMapBBox): ViewBox {
  const width = Math.max(b.max_x - b.min_x, 1e-3)
  const height = Math.max(b.max_y - b.min_y, 1e-3)
  return { minX: b.min_x, minY: -b.max_y, width, height }
}

function ringViewBox(ring: number[][], pad = ZOOM_PAD): ViewBox {
  const xs = ring.map((p) => p[0])
  const ys = ring.map((p) => p[1])
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)
  const w = Math.max(maxX - minX, 1e-3)
  const h = Math.max(maxY - minY, 1e-3)
  const px = w * pad
  const py = h * pad
  return {
    minX: minX - px,
    minY: -(maxY + py),
    width: w + px * 2,
    height: h + py * 2,
  }
}

function ringToPath(ring: number[][]): string {
  return ring
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]},${-p[1]}`)
    .join(' ') + ' Z'
}

/** SVG 屏幕坐标 → 米坐标（考虑 y 翻转）。 */
function screenToMeter(
  svg: SVGSVGElement,
  clientX: number,
  clientY: number,
): [number, number] {
  const pt = svg.createSVGPoint()
  pt.x = clientX
  pt.y = clientY
  const ctm = svg.getScreenCTM()
  if (!ctm) return [0, 0]
  const localPt = pt.matrixTransform(ctm.inverse())
  // SVG y 是翻转的，反回米坐标
  return [localPt.x, -localPt.y]
}

export function SpaceReviewMap({
  jobId,
  selectedKey,
  focusSeq,
  onSelect,
  onShopfrontApplied,
}: Props) {
  const [cadUrl, setCadUrl] = useState<string | null>(null)
  const [cadLoading, setCadLoading] = useState(true)
  const [viewBox, setViewBox] = useState<ViewBox | null>(null)

  // ---- 手动画店铺范围 ----
  const [drawing, setDrawing] = useState(false)
  const [drawPts, setDrawPts] = useState<number[][]>([])
  const [submitting, setSubmitting] = useState(false)
  const svgRef = useRef<SVGSVGElement | null>(null)

  const { data: map, isLoading } = useQuery({
    queryKey: ['review-map', jobId],
    queryFn: () => reviewMapApi.get(jobId),
    enabled: !!jobId,
  })

  useEffect(() => {
    let revoked = false
    setCadLoading(true)
    reviewMapApi
      .reviewPreviewBlob(jobId)
      .then((blob) => {
        const next = URL.createObjectURL(blob)
        if (revoked) {
          URL.revokeObjectURL(next)
          return
        }
        setCadUrl(next)
      })
      .catch(() => undefined)
      .finally(() => {
        if (!revoked) setCadLoading(false)
      })
    return () => {
      revoked = true
    }
  }, [jobId])

  useEffect(() => {
    return () => {
      if (cadUrl) URL.revokeObjectURL(cadUrl)
    }
  }, [cadUrl])

  const fitAll = useMemo(
    () => (map?.bbox ? bboxToViewBox(map.bbox) : null),
    [map?.bbox],
  )

  useEffect(() => {
    if (fitAll && !viewBox) setViewBox(fitAll)
  }, [fitAll, viewBox])

  useEffect(() => {
    if (focusSeq === 0 || !selectedKey || !map) return
    const space = map.spaces.find((s) => s.space_key === selectedKey)
    if (space) setViewBox(ringViewBox(space.ring))
  }, [selectedKey, focusSeq, map])

  const selected = map?.spaces.find((s) => s.space_key === selectedKey) ?? null
  const vb = viewBox ?? fitAll
  const bbox = map?.bbox

  // ---- 画图交互 ----
  const startDrawing = () => {
    setDrawing(true)
    setDrawPts([])
    // 画图前先 fit all，方便看全图
    if (fitAll) setViewBox(fitAll)
  }

  const cancelDrawing = () => {
    setDrawing(false)
    setDrawPts([])
  }

  const undoLastPoint = () => {
    setDrawPts((pts) => pts.slice(0, -1))
  }

  const handleSvgClick = (evt: React.MouseEvent<SVGSVGElement>) => {
    if (!drawing || !svgRef.current) return
    const [x, y] = screenToMeter(svgRef.current, evt.clientX, evt.clientY)
    setDrawPts((pts) => [...pts, [x, y]])
  }

  const submitShopfront = async () => {
    if (drawPts.length < MIN_POLYGON_POINTS) {
      message.warning(`至少需要 ${MIN_POLYGON_POINTS} 个顶点`)
      return
    }
    const ring = [...drawPts, drawPts[0]] // 闭合
    setSubmitting(true)
    try {
      const res = await reviewMapApi.applyShopfront(jobId, {
        type: 'Polygon',
        coordinates: [ring],
      })
      message.success(
        `已按你画的范围复算：保留 ${res.kept} 个空间，标删 ${res.dropped} 个店外空间`,
      )
      setDrawing(false)
      setDrawPts([])
      onShopfrontApplied?.()
    } catch (e) {
      const detail =
        (e as { response?: { data?: { detail?: { error?: { message?: string } } } } })
          .response?.data?.detail?.error?.message ?? '提交失败'
      message.error(detail)
    } finally {
      setSubmitting(false)
    }
  }

  if (!isLoading && map && map.spaces.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="还没有空间几何，无法在图上定位"
      />
    )
  }

  return (
    <div>
      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <Text strong>在 CAD 原图上定位</Text>
          {map?.drawing?.source_label && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                图纸：{map.drawing.source_label}
              </Text>
            </div>
          )}
        </div>
        <Space size={4} wrap>
          {!drawing && (
            <>
              <Tooltip title="显示整张平面">
                <Button size="small" icon={<ExpandOutlined />} onClick={() => fitAll && setViewBox(fitAll)}>
                  全部
                </Button>
              </Tooltip>
              <Tooltip title="在 CAD 底图上画店铺范围，范围外的空间自动排除">
                <Button
                  size="small"
                  type="primary"
                  ghost
                  icon={<BorderOuterOutlined />}
                  onClick={startDrawing}
                >
                  手动画店铺范围
                </Button>
              </Tooltip>
            </>
          )}
          {drawing && (
            <>
              <Tag color="purple">画图模式：点击 CAD 底图添加顶点</Tag>
              <Button
                size="small"
                icon={<UndoOutlined />}
                disabled={drawPts.length === 0}
                onClick={undoLastPoint}
              >
                撤销一个点
              </Button>
              <Popconfirm
                title="确认按当前多边形复算？"
                description={`共 ${drawPts.length} 个顶点。范围外的空间会被标为已删除（可再手工恢复）。`}
                okText="确认"
                cancelText="取消"
                onConfirm={submitShopfront}
                disabled={drawPts.length < MIN_POLYGON_POINTS}
              >
                <Button
                  size="small"
                  type="primary"
                  loading={submitting}
                  disabled={drawPts.length < MIN_POLYGON_POINTS}
                >
                  完成 ({drawPts.length})
                </Button>
              </Popconfirm>
              <Button size="small" icon={<CloseOutlined />} onClick={cancelDrawing}>
                取消
              </Button>
            </>
          )}
        </Space>
      </div>

      {drawing && (
        <Alert
          message="鼠标在 CAD 底图上依次点击 3 个及以上顶点圈出你的店铺范围；点错了用「撤销」；顶点足够后点「完成」提交复算。"
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
        />
      )}

      {!drawing && selected && (
        <div style={{ marginBottom: 8 }}>
          <Tag color="purple">{selected.space_key}</Tag>
          <Text>{selected.name}</Text>
          <Text type="secondary" style={{ marginLeft: 8 }}>
            {SPACE_TYPE_LABELS[selected.space_type as SpaceType] || selected.space_type}
            {' · '}
            {selected.area_m2.toFixed(2)} m²
          </Text>
        </div>
      )}

      <Spin spinning={cadLoading} tip="正在叠合 CAD 原图…">
        <div
          style={{
            position: 'relative',
            height: 560,
            border: '1px solid #f0f0f0',
            borderRadius: 8,
            background: '#fff',
            overflow: 'hidden',
          }}
        >
          {vb && map && bbox && (
            <svg
              ref={svgRef}
              width="100%"
              height="100%"
              viewBox={`${vb.minX} ${vb.minY} ${vb.width} ${vb.height}`}
              preserveAspectRatio="xMidYMid meet"
              style={{
                display: 'block',
                cursor: drawing ? 'crosshair' : 'pointer',
              }}
              onClick={handleSvgClick}
            >
              {cadUrl && (
                <image
                  href={cadUrl}
                  x={bbox.min_x}
                  y={-bbox.max_y}
                  width={bbox.max_x - bbox.min_x}
                  height={bbox.max_y - bbox.min_y}
                  preserveAspectRatio="none"
                />
              )}
              {map.spaces.map((s) => (
                <SpacePolygon
                  key={s.space_key}
                  space={s}
                  selected={s.space_key === selectedKey}
                  drawingMode={drawing}
                  onSelect={onSelect}
                />
              ))}

              {/* 用户画的多边形 */}
              {drawing && drawPts.length > 0 && (
                <ShopfrontDraft points={drawPts} />
              )}
            </svg>
          )}
        </div>
      </Spin>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
        底图是 CAD 原图（按本层平面裁切）。点空间表「定位」或图上色块，会放大到该房间在原图上的位置。
      </Text>
    </div>
  )
}

function ShopfrontDraft({ points }: { points: number[][] }) {
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]},${-p[1]}`)
    .join(' ')
  const closed = points.length >= 3 ? path + ' Z' : path
  // 顶点半径：跟视野无关，用 non-scaling-stroke + 固定 SVG 单位半径
  return (
    <g pointerEvents="none">
      {points.length >= 3 && (
        <path
          d={closed}
          fill="#722ed1"
          fillOpacity={0.12}
          stroke="#531dab"
          strokeWidth={2}
          strokeDasharray="4 3"
          vectorEffect="non-scaling-stroke"
        />
      )}
      {points.length === 2 && (
        <path
          d={path}
          stroke="#531dab"
          strokeWidth={2}
          fill="none"
          strokeDasharray="4 3"
          vectorEffect="non-scaling-stroke"
        />
      )}
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p[0]} cy={-p[1]} r={1} fill="#531dab" />
          <text
            x={p[0]}
            y={-p[1] - 1.4}
            fontSize={1}
            fontWeight={700}
            fill="#531dab"
            textAnchor="middle"
          >
            {i + 1}
          </text>
        </g>
      ))}
    </g>
  )
}

function SpacePolygon({
  space,
  selected,
  drawingMode,
  onSelect,
}: {
  space: ReviewMapSpace
  selected: boolean
  drawingMode: boolean
  onSelect: (key: string) => void
}) {
  const color = SPACE_TYPE_COLORS[space.space_type] || SPACE_TYPE_COLORS.other
  const [cx, cy] = space.centroid
  const xs = space.ring.map((p) => p[0])
  const ys = space.ring.map((p) => p[1])
  const span = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys), 0.5)
  const fontSize = Math.min(span * 0.18, 1.8)
  return (
    <g
      onClick={(evt) => {
        if (drawingMode) return
        evt.stopPropagation()
        onSelect(space.space_key)
      }}
      style={{ cursor: drawingMode ? 'crosshair' : 'pointer' }}
      pointerEvents={drawingMode ? 'none' : 'auto'}
    >
      <title>{`${space.space_key} ${space.name} ${space.area_m2.toFixed(2)} m²`}</title>
      <path
        d={ringToPath(space.ring)}
        fill={color}
        fillOpacity={selected ? 0.38 : 0.12}
        stroke={selected ? '#722ed1' : color}
        strokeWidth={selected ? 3 : 1.4}
        vectorEffect="non-scaling-stroke"
      />
      <text
        x={cx}
        y={-cy}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={fontSize}
        fontWeight={700}
        fill={selected ? '#531dab' : '#1f1f1f'}
        stroke="#fff"
        strokeWidth={fontSize * 0.12}
        paintOrder="stroke"
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {space.space_key}
      </text>
    </g>
  )
}
